/**
 * useAudioFeedback — hook para el audio-feedback de sesión (27 cues del
 * guion `audios_v1`, sección agnóstica). Precarga los 27 clips en un
 * user gesture + expone `play(cue)` disparable desde eventos del
 * state machine.
 *
 * Los mp3 viven en `public/audio/<cueName>.mp3` (sirve Vite como
 * static assets). Nombre del cue === nombre del archivo sin extensión.
 *
 * Mute persistido en localStorage — Ale puede togglearlo una vez y
 * mantenerlo entre sesiones. Volumen respeta el master del sistema
 * (HTMLAudioElement default).
 *
 * NO incluye cues de corrección de forma (Sección 3 del guion) —
 * esperan validación clínica de Juan.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type CueName =
  | "cue_calibrando"
  | "cue_countdown_1"
  | "cue_countdown_2"
  | "cue_countdown_3"
  | "cue_empeza"
  | "cue_num_1"
  | "cue_num_2"
  | "cue_num_3"
  | "cue_num_4"
  | "cue_num_5"
  | "cue_num_6"
  | "cue_num_7"
  | "cue_num_8"
  | "cue_num_9"
  | "cue_num_10"
  | "cue_num_11"
  | "cue_num_12"
  | "cue_num_13"
  | "cue_num_14"
  | "cue_num_15"
  | "cue_una_mas"
  | "cue_ultima"
  | "cue_vas_bien"
  | "cue_serie_completa"
  | "cue_descanso_fin"
  | "cue_sesion_completa"
  | "cue_sin_deteccion";

const ALL_CUES: CueName[] = [
  "cue_calibrando",
  "cue_countdown_1",
  "cue_countdown_2",
  "cue_countdown_3",
  "cue_empeza",
  "cue_num_1",
  "cue_num_2",
  "cue_num_3",
  "cue_num_4",
  "cue_num_5",
  "cue_num_6",
  "cue_num_7",
  "cue_num_8",
  "cue_num_9",
  "cue_num_10",
  "cue_num_11",
  "cue_num_12",
  "cue_num_13",
  "cue_num_14",
  "cue_num_15",
  "cue_una_mas",
  "cue_ultima",
  "cue_vas_bien",
  "cue_serie_completa",
  "cue_descanso_fin",
  "cue_sesion_completa",
  "cue_sin_deteccion",
];

const MUTE_STORAGE_KEY = "gonix_audio_muted";

export interface UseAudioFeedbackReturn {
  /** Reproduce un cue. Si mute activo → no-op. Si no está precargado → no-op. */
  play: (cue: CueName) => void;
  /** Fuerza precarga de los 27 clips. DEBE llamarse desde un user gesture
   *  (click "Empezar sesión") para satisfacer auto-play policy iOS/Safari
   *  + evitar latencia del primer play. Idempotente. */
  preload: () => void;
  /** Estado mute persistido en localStorage. */
  muted: boolean;
  setMuted: (v: boolean) => void;
}

export function useAudioFeedback(): UseAudioFeedbackReturn {
  const [muted, setMutedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(MUTE_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const audiosRef = useRef<Map<CueName, HTMLAudioElement>>(new Map());
  const preloadedRef = useRef(false);
  const mutedRef = useRef(muted);
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const preload = useCallback(() => {
    if (preloadedRef.current) return;
    preloadedRef.current = true;
    for (const cue of ALL_CUES) {
      const audio = new Audio(`/audio/${cue}.mp3`);
      audio.preload = "auto";
      // Load explícito — algunos browsers no fetch hasta el play sin esto.
      audio.load();
      audiosRef.current.set(cue, audio);
    }
  }, []);

  const play = useCallback((cue: CueName) => {
    if (mutedRef.current) return;
    const audio = audiosRef.current.get(cue);
    if (!audio) return;
    // currentTime = 0 reinicia si el mismo cue ya está sonando (raro pero
    // posible con reps rápidas encadenadas). Sin este reset, play() no
    // hace nada en un elemento ya reproduciendo.
    audio.currentTime = 0;
    // Auto-play policy puede rejectar la promesa (iOS sin user gesture,
    // dev tools con audio blocked). Silent fail — no crash de sesión por
    // audio no reproducible.
    audio.play().catch(() => {});
  }, []);

  const setMuted = useCallback((v: boolean) => {
    setMutedState(v);
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, v ? "1" : "0");
    } catch {
      // localStorage disabled (private mode Safari, etc.) — mute funciona
      // en memoria pero no persiste. Aceptable.
    }
    if (v) {
      // Silencia inmediato cualquier cue en curso.
      for (const audio of audiosRef.current.values()) {
        audio.pause();
      }
    }
  }, []);

  // Cleanup al unmount de la sesión — libera memoria + evita audio
  // fantasma si el paciente cierra la pestaña mid-cue.
  useEffect(() => {
    const audios = audiosRef.current;
    return () => {
      for (const audio of audios.values()) {
        audio.pause();
        audio.src = "";
      }
      audios.clear();
    };
  }, []);

  return { play, preload, muted, setMuted };
}
